import Image from "next/image";
import { useCallback, useReducer, useRef } from "react";
import { useDropzone } from "react-dropzone";

import { useAppContext } from "../../_context";
import BrowseToChooseFile from "./BrowseToChooseFile";
import DISPLAY_STATES from "./stateManagement/displayStates";
import {
  DISPLAY_STATE_ACTION,
  ADD_UPLOADS_ACTION,
  REMOVE_UPLOAD_ACTION,
  SET_CROP_ACTION,
  SET_COMPLETED_CROP_ACTION,
  SET_NEW_IMAGE_ACTION,
  RESET_NEW_IMAGE_ACTION,
} from "./stateManagement/uploadActions";
import uploadFileReducer, {
  initialState,
} from "./stateManagement/uploadFileReducer";
import CropModal from "./CropModal";
import { convertToPixelCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

import {
  uploadPhoto,
  handleCropSubmit,
  centerAspectCrop,
} from "../../utilities/Helpers";

const processUpload = async (upload, photoPrefix) => {
  const result = await uploadPhoto({
    file: upload,
    folder: "Coaches",
    prefix: photoPrefix,
  });

  if (!result?.ok) {
    throw new Error("Failed to upload file");
  }

  const { url, largeImgUrl } = await result.json();

  if (!url) {
    throw new Error("Failed to upload file");
  }

  return {
    url,
    largeImgUrl,
  };
};

const UploadFileTarget = ({ className = "", photoPrefix }) => {
  const [{ tx }] = useAppContext();
  const imageToBeCroppedRef = useRef(null);
  const [state, dispatch] = useReducer(uploadFileReducer, initialState);

  const src =
    state.displayState === DISPLAY_STATES.HAS_UPLOAD
      ? state.url
      : "/img/icons/add-photo.png";
  const size = state.displayState === DISPLAY_STATES.HAS_UPLOAD ? 120 : 48;
  const opts =
    state.displayState === DISPLAY_STATES.HAS_UPLOAD
      ? { objectFit: "cover" }
      : {};
  const isDragAndDropActive =
    state.displayState === DISPLAY_STATES.INITIAL ||
    state.displayState === DISPLAY_STATES.HAS_UPLOAD;

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length) {
      dispatch({ type: SET_CROP_ACTION, crop: initialState.crop });
      const reader = new FileReader();
      reader.readAsDataURL(acceptedFiles[0]);
      reader.onload = () =>
        dispatch({ type: SET_NEW_IMAGE_ACTION, newImage: reader.result });
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
    accept: "image/png, image/jpeg, image/jpg, image/webp",
  });
  const dropProps = getRootProps();
  const onClick = dropProps.onClick;
  const spinner = (
    <div className="text-primary">
      <span className="spinner-border" />
    </div>
  );
  delete dropProps.onClick;

  const getTitle = () => {
    switch (state.displayState) {
      case DISPLAY_STATES.UPLOADING: {
        return `${tx["Uploading"]}...`;
      }
      case DISPLAY_STATES.HAS_ERROR: {
        return (
          <>
            <span className="text-red">
              {tx["There was an error uploading your file."]}{" "}
            </span>
            <span
              className="text-primary fw-bold cursor-pointer"
              onClick={onClick}>
              {tx["Please try again"]}
            </span>
            .
          </>
        );
      }
      case DISPLAY_STATES.HAS_UPLOAD: {
        return (
          <>
            <span
              className="text-red fw-bold cursor-pointer"
              onClick={() => dispatch({ type: REMOVE_UPLOAD_ACTION })}>
              {tx["Remove"]}
            </span>
          </>
        );
      }
      default: {
        return (
          <>
            <p className="mb-0">
              <strong>*</strong> {tx["Drag and drop photo to upload"]}
            </p>
            <BrowseToChooseFile onClick={onClick} />
          </>
        );
      }
    }
  };

  const showCropModal = !!(
    state.newImage && state.displayState !== DISPLAY_STATES.HAS_ERROR
  );
  const onImageToBeCroppedLoad = (e) => {
    const { naturalWidth, naturalHeight, width, height } = e.currentTarget;
    const centeredCrop = centerAspectCrop(naturalWidth, naturalHeight, 1);

    dispatch({
      type: SET_CROP_ACTION,
      crop: centeredCrop,
      newImageWidth: naturalWidth,
      newImageHeight: naturalHeight,
    });
    dispatch({
      type: SET_COMPLETED_CROP_ACTION,
      completedCrop: convertToPixelCrop(centeredCrop, width, height),
    });
  };

  const uploadNewImage = async (toUpload) => {
    try {
      const newPhotoUpload = await processUpload(toUpload, photoPrefix);

      dispatch({ type: RESET_NEW_IMAGE_ACTION });
      dispatch({
        type: ADD_UPLOADS_ACTION,
        url: newPhotoUpload.url,
        largeAvatarUrl: newPhotoUpload.largeImgUrl,
      });
    } catch (e) {
      console.log("error::", e);

      dispatch({
        type: DISPLAY_STATE_ACTION,
        newState: DISPLAY_STATES.HAS_ERROR,
      });
    }
  };
  const handleAddPhotoClick = () => {
    if (state.displayState === DISPLAY_STATES.UPLOADING) return;

    dispatch({
      type: DISPLAY_STATE_ACTION,
      newState: DISPLAY_STATES.UPLOADING,
    });
    handleCropSubmit({
      currentImage: imageToBeCroppedRef?.current,
      completedCrop: state.completedCrop,
      cb: uploadNewImage,
    });
  };

  return (
    <div className={`upload-file-target-container ${className}`}>
      <CropModal
        aspect={1}
        open={showCropModal}
        crop={state.crop}
        disabled={state.displayState === DISPLAY_STATES.UPLOADING}
        isLoading={state.displayState === DISPLAY_STATES.UPLOADING}
        imgRef={imageToBeCroppedRef}
        onCropChange={(c) => dispatch({ type: SET_CROP_ACTION, crop: c })}
        onCropComplete={(c) =>
          dispatch({ type: SET_COMPLETED_CROP_ACTION, completedCrop: c })
        }
        newImage={state.newImage}
        onNewImageLoad={onImageToBeCroppedLoad}
        onAddPhoto={handleAddPhotoClick}
        onCancel={() => dispatch({ type: RESET_NEW_IMAGE_ACTION })}
      />
      <input type="hidden" id="signup-avatar-url" value={state.url || ""} />
      <input
        type="hidden"
        id="signup-large-avatar-url"
        value={state.largeAvatarUrl || ""}
      />
      <div
        className={`upload-file-target overflow-hidden ${
          state?.url ? "border-0" : ""
        }`}
        {...(isDragAndDropActive ? getRootProps() : {})}>
        <input {...getInputProps()} />
        {state.displayState === DISPLAY_STATES.UPLOADING ? (
          spinner
        ) : (
          <Image
            src={src}
            width={size}
            height={size}
            objectPosition="center"
            {...opts}
            unoptimized={true}
          />
        )}
      </div>
      {getTitle()}
    </div>
  );
};

export default UploadFileTarget;
